// Reconstruction des trades Quantower à partir des exécutions.
//
// Quantower exporte des ORDRES EXÉCUTÉS, pas des trades : une ligne ne porte
// jamais à la fois le prix d'entrée et celui de sortie. Le `Gross P/L` n'est
// renseigné que sur les lignes qui ferment tout ou partie d'une position.
//
// Un mapping ligne-à-ligne (celui des autres dialectes) produirait donc deux
// fois trop de trades, la moitié à P&L nul et sans prix de sortie — win rate,
// durée et R:R faussés. On apparie les exécutions en FIFO pour reconstituer de
// vrais allers-retours.
//
// Module volontairement pur : pas de CSV, pas de dates parsées, pas d'I/O.
// Toute la logique risquée vit ici et se teste isolément.

/** Une exécution : un ordre passé. `quantity` porte le signe (positif = achat). */
export interface QuantowerExecution {
  /** Horodatage ISO trié lexicographiquement (YYYY-MM-DDTHH:MM:SS). */
  time: string;
  symbol: string;
  quantity: number;
  price: number;
  /** Frais de l'exécution, positif tel qu'exporté par Quantower. */
  fee: number;
  /** P&L réalisé par cette exécution (0 sur une ouverture). */
  grossPnl: number;
}

/** Un trade reconstruit, au vocabulaire du format natif Edgely. */
export interface MatchedTrade {
  symbol: string;
  type: "long" | "short";
  status: "open" | "closed";
  quantity: number;
  entryPrice: number;
  exitPrice: number | null;
  entryDate: string;
  exitDate: string | null;
  /** P&L réalisé, null tant que la position est ouverte. */
  pnl: number | null;
  /** Convention projet : NÉGATIF (netPnl = pnl + commission + swaps). */
  commission: number;
}

export interface FifoResult {
  trades: MatchedTrade[];
  errors: Array<{ row: number; error: string }>;
}

/** Un lot encore ouvert, en attente d'être soldé. */
interface OpenLot {
  time: string;
  price: number;
  /** Quantité restante, toujours positive. */
  remaining: number;
  /** Frais de l'exécution d'entrée, répartis au prorata des sorties. */
  feePerUnit: number;
  /** Ordre d'arrivée, pour restituer les trades dans l'ordre du fichier. */
  seq: number;
}

/**
 * Frais d'une exécution ramenés à l'unité, pour être répartis quand une
 * exécution solde plusieurs lots (ou l'inverse). Sans ce prorata, une sortie
 * groupée verrait ses frais comptés une fois par lot et le total importé
 * dépasserait celui du fichier.
 */
function perUnit(fee: number, quantity: number): number {
  const qty = Math.abs(quantity);
  return qty === 0 ? 0 : Math.abs(fee) / qty;
}

/**
 * Arrondit au centime. Le prorata des frais et du P&L produit sinon des restes
 * flottants (-0.30000000000000004) qui partent tels quels en base et ressortent
 * dans les exports et les infobulles.
 */
function money(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Apparie les exécutions en trades, position par position et symbole par
 * symbole.
 *
 * Règles :
 * - FIFO : le lot ouvert le plus ancien est soldé en premier.
 * - Une fermeture partielle produit un trade par appariement (décision de
 *   design : granularité fine, le total du P&L reste celui du fichier).
 * - Un retournement (vendre plus que la position longue) solde le long puis
 *   ouvre un short avec le reliquat.
 * - Les positions encore ouvertes en fin de fichier sont remontées en
 *   `status: "open"` plutôt que perdues.
 * - Une fermeture sans position correspondante ne peut pas être inventée :
 *   elle est signalée en erreur.
 */
export function matchExecutionsFifo(executions: QuantowerExecution[]): FifoResult {
  const trades: MatchedTrade[] = [];
  const errors: Array<{ row: number; error: string }> = [];

  // Positions ouvertes par symbole. Le signe des lots est porté par `side`.
  const books = new Map<string, { side: "long" | "short"; lots: OpenLot[] }>();

  // Les exports Quantower listent du plus récent au plus ancien ; l'appariement
  // FIFO n'a de sens que chronologiquement. Tri stable sur l'horodatage ISO.
  const ordered = executions
    .map((execution, index) => ({ execution, index }))
    .sort((a, b) =>
      a.execution.time === b.execution.time
        ? a.index - b.index
        : a.execution.time < b.execution.time
          ? -1
          : 1,
    );

  let seq = 0;

  for (const { execution, index } of ordered) {
    const { symbol, quantity, price, time } = execution;
    if (quantity === 0) continue;

    const side: "long" | "short" = quantity > 0 ? "long" : "short";
    let remaining = Math.abs(quantity);
    const closingFeePerUnit = perUnit(execution.fee, quantity);

    // Le P&L de l'exécution couvre l'ensemble de ce qu'elle solde ; on le
    // répartit au prorata pour que la somme des trades égale le fichier.
    const closedByThis = (() => {
      const book = books.get(symbol);
      if (!book || book.side === side) return 0;
      const open = book.lots.reduce((sum, lot) => sum + lot.remaining, 0);
      return Math.min(open, remaining);
    })();
    const pnlPerUnit = closedByThis === 0 ? 0 : execution.grossPnl / closedByThis;

    const book = books.get(symbol);

    // Sens opposé à la position courante : on solde, du plus ancien au plus récent.
    if (book && book.side !== side && book.lots.length > 0) {
      while (remaining > 0 && book.lots.length > 0) {
        const lot = book.lots[0];
        const size = Math.min(lot.remaining, remaining);

        trades.push({
          symbol,
          type: book.side,
          status: "closed",
          quantity: size,
          entryPrice: lot.price,
          exitPrice: price,
          entryDate: lot.time,
          exitDate: time,
          pnl: money(pnlPerUnit * size),
          // Frais d'entrée (prorata) + frais de sortie (prorata), en négatif.
          commission: money(-(lot.feePerUnit * size + closingFeePerUnit * size)),
        });

        lot.remaining -= size;
        remaining -= size;
        if (lot.remaining === 0) book.lots.shift();
      }

      if (book.lots.length === 0) books.delete(symbol);
    }

    if (remaining === 0) continue;

    // Reliquat : soit une ouverture simple, soit le retournement d'une position
    // qu'on vient de solder entièrement.
    const current = books.get(symbol);

    if (current && current.side !== side) {
      // Ne devrait pas arriver : la boucle ci-dessus vide le carnet avant de
      // rendre la main. Garde-fou plutôt qu'un état incohérent silencieux.
      errors.push({
        row: index + 2,
        error: `Exécution ${symbol} incohérente avec la position courante`,
      });
      continue;
    }

    if (!current) {
      // Le reliquat porte un P&L réalisé : il prétend fermer quelque chose qui
      // n'existe pas dans le fichier (export démarré en cours de position).
      // L'ouvrir comme une nouvelle position inventerait une entrée au prix de
      // sortie et fausserait le P&L — on signale plutôt.
      //
      // Un retournement légitime est exclu : il a consommé des lots
      // (closedByThis > 0), donc son P&L est déjà entièrement attribué aux
      // trades soldés ci-dessus et le reliquat est une ouverture normale.
      if (closedByThis === 0 && execution.grossPnl !== 0) {
        errors.push({
          row: index + 2,
          error:
            `Fermeture de ${remaining - closedByThis} ${symbol} sans position ouverte ` +
            `correspondante (l'export commence probablement en cours de position)`,
        });
        continue;
      }
      books.set(symbol, { side, lots: [] });
    }

    books.get(symbol)!.lots.push({
      time,
      price,
      remaining,
      feePerUnit: closingFeePerUnit,
      seq: seq++,
    });
  }

  // Ce qui reste ouvert en fin de fichier est un trade en cours, pas un déchet.
  for (const [symbol, book] of books) {
    for (const lot of book.lots) {
      trades.push({
        symbol,
        type: book.side,
        status: "open",
        quantity: lot.remaining,
        entryPrice: lot.price,
        exitPrice: null,
        entryDate: lot.time,
        exitDate: null,
        pnl: null,
        commission: money(-(lot.feePerUnit * lot.remaining)),
      });
    }
  }

  return { trades, errors };
}
